import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ReassignCustomerPostRequestBody,
  ReassignCustomerPreviewResponse,
  ReassignCustomerResponse,
} from '@lio/api-types';
import { useClient } from '@utils/api-client';
import { quoteItemQueryKey } from '@services/quoteItem';
import { quoteFormQueryKeys } from '@services/quoteForm';
import { quoteQueryKey } from '@services/quotes/keys';

// Re-export the published reassign-customer types so consumers can import them from the
// service barrel (`@services/quotes`) alongside the hooks.
export type {
  ReassignCustomerContactField,
  ReassignCustomerContactChoice,
  ReassignCustomerContactCompareRow,
  ReassignCustomerBorConflict,
  ReassignCustomerPreviewResponse,
  ReassignCustomerPostRequestBody,
} from '@lio/api-types';

// Local augmentation for the customer-access fields added in LIO-28910. These are
// also exported from `@lio/api-types`, but the portal picks up the published package
// on its own cadence; defining them here keeps the portal compiling until that bump
// lands. TODO(LIO-28910): drop these once the api-types version is republished.
export interface ReassignCustomerUserAccess {
  userId: string;
  name: string;
  email: string;
}
export interface ReassignCustomerAccessInfo {
  customerUsers: ReassignCustomerUserAccess[];
  pendingInviteCount: number;
}
export type ReassignCustomerPreviewResponseWithAccess = ReassignCustomerPreviewResponse & {
  access: ReassignCustomerAccessInfo;
};

/** Preview request: quote id (for the URL) + the target to evaluate. */
export interface ReassignQuotePreviewRequest {
  quoteId: string;
  targetCustomerId: string;
}

/** Execute request: quote id (for the URL) + the API request body. */
export type ReassignQuoteRequest = { quoteId: string } & ReassignCustomerPostRequestBody & {
    /** All-or-none: keep existing customer-user access after the move. */
    keepCustomerUserAccess?: boolean;
  };

/** Dry-run: returns BOR conflict, per-field contact compare, and customer-access info. */
export const useReassignQuotePreview = () => {
  const client = useClient();
  const mutationFn = async ({ quoteId, targetCustomerId }: ReassignQuotePreviewRequest) =>
    client
      .post(`quote/${quoteId}/reassign-customer/preview`, { json: { targetCustomerId } })
      .json<ReassignCustomerPreviewResponseWithAccess>();
  const result = useMutation({ mutationFn });
  return { ...result, previewReassignQuote: result.mutateAsync };
};

/** Executes the reassignment. */
export const useReassignQuote = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const mutationFn = async ({ quoteId, ...body }: ReassignQuoteRequest) =>
    client.post(`quote/${quoteId}/reassign-customer`, { json: body }).json<ReassignCustomerResponse>();
  const result = useMutation({
    mutationFn,
    onSuccess: async (_data, request) => {
      // The dialog relies on these invalidations (not a full page reload) to refresh
      // the quote view after the move — customer, named insured, and status changed.
      await queryClient.invalidateQueries({ queryKey: quoteQueryKey.get(request.quoteId) });
      await queryClient.invalidateQueries({
        queryKey: quoteItemQueryKey.searchWithParams({ quoteId: request.quoteId }),
      });
      await queryClient.invalidateQueries({ queryKey: quoteFormQueryKeys.get(request.quoteId) });
    },
  });
  return { ...result, reassignQuote: result.mutateAsync };
};
